import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneCall, PhoneOff, Clock, CheckCircle, XCircle, ArrowLeft, Mic, MicOff, Wifi, WifiOff } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Button, LoadingSpinner } from './components';

// Plivo Browser SDK will be loaded from CDN
const PLIVO_SDK_URL = 'https://cdn.plivo.com/sdk/browser/v2/plivo.min.js';

function MakeCallTestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const [error, setError] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Plivo SDK state
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkConnected, setSdkConnected] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const plivoRef = useRef(null);

  // Pre-populate phone number from URL params (e.g., from client profile)
  useEffect(() => {
    const phoneFromUrl = searchParams.get('phone');
    if (phoneFromUrl) {
      // Extract digits and format
      const digits = phoneFromUrl.replace(/\D/g, '');
      if (digits.length >= 10) {
        const lastTen = digits.slice(-10);
        const formatted = `(${lastTen.slice(0, 3)}) ${lastTen.slice(3, 6)}-${lastTen.slice(6, 10)}`;
        setPhoneNumber(formatted);
      }
    }
  }, [searchParams]);

  // Load Plivo SDK on mount
  useEffect(() => {
    const loadPlivoSDK = () => {
      if (window.Plivo) {
        setSdkLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = PLIVO_SDK_URL;
      script.async = true;
      script.onload = () => {
        console.log('Plivo SDK loaded');
        setSdkLoaded(true);
      };
      script.onerror = () => {
        setSdkError('Failed to load Plivo SDK');
      };
      document.body.appendChild(script);
    };

    loadPlivoSDK();
  }, []);

  // Initialize Plivo client when SDK is loaded
  useEffect(() => {
    if (!sdkLoaded || !window.Plivo) return;

    const initPlivo = async () => {
      try {
        // Get credentials from our API
        const credentials = await apiService.get('/amn/calling/token/');

        console.log('Plivo: Got credentials', { username: credentials.username });

        // Initialize Plivo client (SDK v2 - events registered separately via EventEmitter)
        const plivoOptions = {
          debug: 'ALL',
          permOnClick: true,
          enableTracking: false,
          closeProtection: true,
          maxAverageBitrate: 48000,
        };

        const plivoClient = new window.Plivo(plivoOptions);
        plivoRef.current = plivoClient;

        // Register event handlers via EventEmitter pattern (client.on)
        plivoClient.client.on('onWebrtcNotSupported', () => {
          console.error('Plivo: WebRTC not supported');
          setSdkError('WebRTC not supported in this browser');
        });

        plivoClient.client.on('onLogin', () => {
          console.log('Plivo: Login SUCCESS');
          if (plivoRef.current?._loginTimeout) {
            clearTimeout(plivoRef.current._loginTimeout);
          }
          setSdkConnected(true);
          setSdkError(null);
        });

        plivoClient.client.on('onLoginFailed', (cause) => {
          console.error('Plivo: Login FAILED:', cause);
          if (plivoRef.current?._loginTimeout) {
            clearTimeout(plivoRef.current._loginTimeout);
          }
          setSdkError(`Failed to connect: ${cause || 'Unknown error'}`);
          setSdkConnected(false);
        });

        plivoClient.client.on('onCallRemoteRinging', (callInfo) => {
          console.log('Plivo: Remote ringing', callInfo);
          setCallStatus(prev => prev ? { ...prev, status: 'ringing' } : null);
        });

        plivoClient.client.on('onCallAnswered', (callInfo) => {
          console.log('Plivo: Call answered', callInfo);
          setInCall(true);
          setCallStatus(prev => prev ? { ...prev, status: 'answered' } : null);
        });

        plivoClient.client.on('onCallTerminated', (callInfo, cause) => {
          console.log('Plivo: Call terminated', cause);
          setInCall(false);
          setLoading(false);
          setCallStatus(prev => prev ? { ...prev, status: 'completed' } : null);
          // Clear call status after 3 seconds
          setTimeout(() => setCallStatus(null), 3000);
          setTimeout(fetchCallHistory, 2000);
        });

        plivoClient.client.on('onCallFailed', (cause) => {
          console.error('Plivo: Call failed', cause);
          setInCall(false);
          setLoading(false);
          setCallStatus(prev => prev ? { ...prev, status: 'failed' } : null);
          setError(`Call failed: ${cause}`);
          // Clear call status after 5 seconds
          setTimeout(() => setCallStatus(null), 5000);
        });

        plivoClient.client.on('onMediaPermission', (isAllowed) => {
          console.log('Plivo: Media permission:', isAllowed);
          if (!isAllowed) {
            setSdkError('Microphone permission denied. Please allow microphone access.');
          }
        });

        // Login to Plivo (just username, SDK adds domain automatically)
        console.log('Plivo: Logging in as', credentials.username);

        // Set a timeout to detect stuck login
        const loginTimeout = setTimeout(() => {
          console.error('Plivo: Login timeout - no response after 10 seconds');
          if (plivoClient.client) {
            console.log('Plivo: isRegistered:', plivoClient.client.isRegistered());
            console.log('Plivo: isConnected:', plivoClient.client.isConnected());
            console.log('Plivo: isConnecting:', plivoClient.client.isConnecting());
          }
          setSdkError('Login timeout - check console for details');
        }, 10000);

        plivoClient._loginTimeout = loginTimeout;

        plivoClient.client.login(credentials.username, credentials.password);

      } catch (err) {
        console.error('Failed to initialize Plivo:', err);
        setSdkError(err.message || 'Failed to initialize calling service');
      }
    };

    initPlivo();

    return () => {
      if (plivoRef.current) {
        try {
          plivoRef.current.client.logout();
        } catch (e) {
          // Ignore logout errors on cleanup
        }
      }
    };
  }, [sdkLoaded]);

  useEffect(() => {
    fetchCallHistory();
  }, []);

  const fetchCallHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await apiService.get('/amn/calling/history/');
      setCallHistory(response.results || response || []);
    } catch (err) {
      console.error('Failed to fetch call history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleInitiateCall = async () => {
    if (!phoneNumber) {
      setError('Please enter a phone number');
      return;
    }

    // Extract digits for API
    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!sdkConnected || !plivoRef.current) {
      setError('Not connected to calling service. Please wait or refresh the page.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setCallStatus(null);

      // Format number for Plivo (E.164 format)
      const formattedNumber = `+1${digits}`;

      // Make the call via Plivo SDK directly
      // The SDK handles WebRTC connection and dialing
      // Our webhooks will log the call when it connects
      const extraHeaders = {
        'X-PH-to_number': formattedNumber,
      };

      plivoRef.current.client.call(formattedNumber, extraHeaders);

      setCallStatus({
        success: true,
        status: 'initiated',
        toNumber: formattedNumber,
      });

    } catch (err) {
      setError(err.message || 'Failed to initiate call');
      setCallStatus({ success: false });
      setLoading(false);
    }
  };

  const handleHangup = () => {
    if (plivoRef.current && inCall) {
      plivoRef.current.client.hangup();
    }
    setInCall(false);
    setCallStatus(prev => prev ? { ...prev, status: 'completed' } : null);
    setLoading(false);
  };

  const toggleMute = () => {
    if (plivoRef.current && inCall) {
      if (isMuted) {
        plivoRef.current.client.unmute();
      } else {
        plivoRef.current.client.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      initiated: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      ringing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: PhoneCall },
      answered: { bg: 'bg-green-100', text: 'text-green-800', icon: PhoneCall },
      completed: { bg: 'bg-gray-100', text: 'text-gray-800', icon: CheckCircle },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.initiated;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto">
      <div className="p-3">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-neutral-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <h1 className="text-xl font-semibold text-neutral-900">Make Test Call</h1>
        </div>

        {/* Connection Status */}
        <div className={`mb-3 p-3 rounded-lg flex items-center gap-2 ${
          sdkConnected ? 'bg-green-50 text-green-800' :
          sdkError ? 'bg-red-50 text-red-800' :
          'bg-yellow-50 text-yellow-800'
        }`}>
          {sdkConnected ? (
            <>
              <Wifi className="w-4 h-4" />
              <span className="text-sm font-medium">Connected to calling service</span>
            </>
          ) : sdkError ? (
            <>
              <WifiOff className="w-4 h-4" />
              <span className="text-sm">{sdkError}</span>
            </>
          ) : (
            <>
              <LoadingSpinner size="sm" />
              <span className="text-sm">Connecting to calling service...</span>
            </>
          )}
        </div>

        {/* Call Form */}
        <Panel className="mb-3">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-5 h-5 text-red-800" />
            <h2 className="text-lg font-medium">Initiate Outgoing Call</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="(321) 555-1234"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                maxLength={14}
              />
              <p className="text-xs text-neutral-500 mt-1">
                Enter a US phone number. Call will be made from 321-320-9044.
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {callStatus?.success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-700 font-medium">Call Initiated!</p>
                    <p className="text-green-600 text-sm">Call ID: {callStatus.callId}</p>
                  </div>
                  {getStatusBadge(callStatus.status)}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              {!inCall ? (
                <Button
                  onClick={handleInitiateCall}
                  disabled={loading || !phoneNumber || !sdkConnected}
                  className="flex items-center gap-2"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <PhoneCall className="w-4 h-4" />
                  )}
                  {loading ? 'Calling...' : 'Make Call'}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={toggleMute}
                    variant="secondary"
                    className="flex items-center gap-2"
                  >
                    {isMuted ? (
                      <><MicOff className="w-4 h-4" /> Unmute</>
                    ) : (
                      <><Mic className="w-4 h-4" /> Mute</>
                    )}
                  </Button>
                  <Button
                    onClick={handleHangup}
                    variant="danger"
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <PhoneOff className="w-4 h-4" />
                    Hang Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </Panel>

        {/* Call History */}
        <Panel>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Recent Calls</h2>
            <button
              onClick={fetchCallHistory}
              className="text-sm text-red-800 hover:text-red-900"
            >
              Refresh
            </button>
          </div>

          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : callHistory.length === 0 ? (
            <p className="text-neutral-500 text-center py-8">No calls yet</p>
          ) : (
            <div className="space-y-2">
              {callHistory.map((call) => (
                <div
                  key={call.id}
                  className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-neutral-400" />
                    <div>
                      <p className="font-medium text-neutral-900">{call.to_number}</p>
                      <p className="text-xs text-neutral-500">
                        {new Date(call.initiated_at).toLocaleString()}
                        {call.duration_seconds > 0 && ` • ${formatDuration(call.duration_seconds)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(call.status)}
                    {call.transcription_status === 'completed' && (
                      <span className="text-xs text-green-600">Transcribed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

export default MakeCallTestPage;
